import {OverlayModule} from '@angular/cdk/overlay';
import {ChangeDetectionStrategy, Component, computed, ElementRef, forwardRef, inject, input, signal, viewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import moment, {Moment} from 'moment';
import {DateFormatService} from '../date-format/date-format.service';
import {DatePickerComponent} from '../date-picker/date-picker.component';
import {OverlayPanelDirective} from '../overlay/overlay-panel.directive';
import {DATE_INPUT_FORMATS, DATE_PICKER_POSITIONS, ISO_DATE_FORMAT, US_DATE_INPUT_FORMATS} from './date-input.constants';

/**
 * A single-date form field: a typable input that also opens the {@link DatePickerComponent} in a
 * popup, with a calendar icon. Set `allowTyping` false for a pick-only variant, which forbids
 * typing and shows a clear button instead — and `clearable` false on top of that for a pick-only
 * field that is required, leaving no way to empty it. A pick-only field also takes a `displayFormat`
 * of its own. A drop-in for a native `input[type=date]` — it is a
 * `ControlValueAccessor` speaking the same `YYYY-MM-DD` string, so `formControl` /
 * `formControlName` keep working unchanged.
 */
@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss',
  imports: [OverlayModule, OverlayPanelDirective, DatePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-date-input'},
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateInputComponent),
    multi: true,
  }],
})
export class DateInputComponent implements ControlValueAccessor {
  /** Bounds passed through to the calendar and enforced on typed dates. Unbounded by default. */
  readonly min = input<Moment | null>(null);
  readonly max = input<Moment | null>(null);
  /** Shown greyed while no date is set. Defaults to today in whatever format the field reads in. */
  readonly placeholder = input<string | null>(null);
  /** On by default: a date can be typed as well as picked. Set false for a pick-only field — the
   *  calendar only, with a clear button (since typing can no longer empty it). */
  readonly allowTyping = input(true);
  /** Set false on a required pick-only field: there is then no way to empty it, which is the point.
   *  Nothing to turn off while typing is allowed — the clear button only exists without it. */
  readonly clearable = input(true);
  /**
   * What the field shows, in moment tokens — `MMM D YYYY` for a field that reads `Sep 15 2026`.
   * `null` (the default) is the company profile's `date` format: `2026.09.15`, or `Sep 15 2026`.
   *
   * Honoured on a pick-only field only. A typeable one has to render exactly what it parses back
   * (the profile's `date` format always parses), or the value on screen is one the user cannot
   * retype; a pick-only field never parses its own text, so it is free to read however its caller
   * wants.
   */
  readonly displayFormat = input<string | null>(null);

  protected readonly positions = DATE_PICKER_POSITIONS;

  private readonly formats = inject(DateFormatService);
  /** What typing is parsed against: the profile's own `date` format is always among them. */
  private readonly inputFormats = computed(() =>
    this.formats.profile() === 'us' ? [...US_DATE_INPUT_FORMATS, ...DATE_INPUT_FORMATS] : DATE_INPUT_FORMATS);
  /** The US format spells the month out, so a phone keyboard needs letters there. */
  protected readonly inputMode = computed(() => this.formats.profile() === 'us' ? 'text' : 'numeric');

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('input');

  protected readonly value = signal<Moment | null>(null);
  protected readonly open = signal(false);
  protected readonly disabled = signal(false);
  /** Raw text while the user is typing; `null` when the field is showing the committed value. */
  private readonly draft = signal<string | null>(null);
  /** The current text does not parse — for the error fill, feedback only. */
  protected readonly invalid = signal(false);

  /** {@link displayFormat}, except where typing forces the field to render what it parses. */
  private readonly format = computed(() => {
    const profileFormat = this.formats.pattern('date');
    return this.allowTyping() ? profileFormat : this.displayFormat() ?? profileFormat;
  });
  protected readonly placeholderText = computed(() => this.placeholder() ?? this.formats.now().format(this.format()));
  private readonly display = computed(() => this.value()?.format(this.format()) ?? '');
  /** What the input shows: the in-progress draft, else the committed value formatted. */
  protected readonly text = computed(() => this.draft() ?? this.display());
  /** Pick-only fields cannot be emptied by typing, so they get a clear button when they hold a
   *  value — unless the field is required and `clearable` turns it off. When typing is allowed,
   *  deleting the text clears it instead. */
  protected readonly showClear = computed(() =>
    this.clearable() && !this.allowTyping() && !this.disabled() && !!this.value());

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    // Strict, explicit formats — a bare `moment(string)` warns and falls back to `new Date()` for
    // anything it does not read as ISO. Accepts the backend's ISO date(-time) and any typed format.
    const parsed = value ? moment(value, [moment.ISO_8601, ...this.inputFormats()], true) : null;
    this.value.set(parsed?.isValid() ? parsed : null);
    this.draft.set(null);
    this.invalid.set(false);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  /** Clicking the field focuses the input — selecting its text, so typing replaces, when typing is
   *  allowed — and opens the calendar. Same "field is the trigger" behaviour as the period selector. */
  protected openPicker(): void {
    if (this.disabled() || this.open()) {
      return;
    }
    const input = this.inputRef()?.nativeElement;
    input?.focus();
    // Pick-only field: it is a trigger, not a text box, so there is nothing to select.
    if (this.allowTyping()) {
      input?.select();
    }
    this.open.set(true);
  }

  /** Backdrop click or detach — also the field's first "touched", like blurring a native input. */
  protected closePicker(): void {
    if (this.open()) {
      this.open.set(false);
      this.onTouched();
    }
  }

  protected type(text: string): void {
    this.draft.set(text);
    this.invalid.set(text.trim() !== '' && this.parse(text) === null);
  }

  /** Commits the typed text on blur/Enter: empty clears the value, a parseable date sets it, and
   *  anything else is dropped — the field reverts as `text` recomputes to the committed value.
   *  Whether an empty value is allowed is the form control's own `required` validator to decide. */
  protected commit(): void {
    if (!this.allowTyping()) {
      return; // pick-only: nothing can be typed, so blur/Enter have nothing to commit
    }
    const text = this.draft();
    this.draft.set(null);
    this.invalid.set(false);
    this.onTouched();
    if (text === null) {
      return;
    }
    if (text.trim() === '') {
      this.setValue(null);
      this.open.set(false); // emptied by typing — close the calendar too
      return;
    }
    const parsed = this.parse(text);
    if (parsed) {
      this.setValue(parsed);
      this.open.set(false); // a date was chosen by typing — close the calendar, as picking one does
    }
  }

  /** Enter commits without submitting the form the field may sit in. */
  protected commitOnEnter(event: Event): void {
    event.preventDefault();
    this.commit();
  }

  protected select(date: Moment): void {
    this.draft.set(null);
    this.invalid.set(false);
    this.setValue(date);
    this.open.set(false);
    this.onTouched();
  }

  protected clear(): void {
    this.setValue(null);
    this.onTouched();
  }

  private setValue(date: Moment | null): void {
    this.value.set(date?.clone() ?? null);
    this.onChange(date ? date.format(ISO_DATE_FORMAT) : null);
  }

  /** A typed date in any tolerated format, within `min`/`max`, or `null`. */
  private parse(text: string): Moment | null {
    const parsed = moment(text, this.inputFormats(), true);
    if (!parsed.isValid()) {
      return null;
    }
    const min = this.min();
    const max = this.max();
    if (min && parsed.isBefore(min, 'day')) return null;
    return max && parsed.isAfter(max, 'day') ? null : parsed;
  }
}
